/**
 * PMActiveProjects - Tab 4: Active Projects in Production
 *
 * Lifecycle filter: pm_readiness = 'WO_PUBLISHED'
 *
 * Shows projects currently in production with:
 * - Real-time production progress via MilestoneGrid
 * - Active worker indicators
 * - PM actions: Approve PDF, Mark Complete (→ Tab 5)
 */

import { useState, useEffect, useCallback } from "react";
import MilestoneGrid from "../project/MilestoneGrid";

export default function PMActiveProjects({ acquireAccessToken, lifecycleFilter = "WO_PUBLISHED" }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  // Fetch projects in WO_PUBLISHED state
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const token = await acquireAccessToken();
      if (!token) {
        setError("Authentication required");
        return;
      }
      setAccessToken(token);

      const params = new URLSearchParams();
      if (lifecycleFilter) {
        params.set("pmReadiness", lifecycleFilter);
      }
      const url = `/api/pm-center/projects/my-projects${params.toString() ? `?${params}` : ""}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch projects");
      }

      const data = await res.json();
      setProjects(data.projects || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching active projects:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [acquireAccessToken, lifecycleFilter]);

  // Load projects on mount
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Handle marking project complete (→ READY_FOR_BILLING → Tab 5)
  const handleMarkComplete = useCallback(async (project) => {
    const confirmed = confirm(
      `Mark this project as complete?\n\n` +
      `Project: ${project.quote_number} - ${project.customer_name}\n\n` +
      `This will move the project to Completed (Tab 5).`
    );
    if (!confirmed) return;

    try {
      const token = await acquireAccessToken();
      if (!token) return;

      const res = await fetch(
        `/api/pm-center/projects/${encodeURIComponent(project.quote_number)}/complete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Failed to mark complete");
      }

      alert("Project marked complete! It will now appear in the Completed tab.");
      setSelectedProject(null);
      fetchProjects();
    } catch (err) {
      console.error("Error marking complete:", err);
      alert(`Error: ${err.message}`);
    }
  }, [acquireAccessToken, fetchProjects]);

  // Handle PDF approval action
  const handleApprovePDF = useCallback(async (project) => {
    const confirmed = confirm(
      `Approve PDF for this project?\n\n` +
      `Project: ${project.quote_number} - ${project.customer_name}\n\n` +
      `This clears any PDF approval blocker.`
    );
    if (!confirmed) return;

    try {
      const token = await acquireAccessToken();
      if (!token) return;

      const res = await fetch(
        `/api/pm-center/projects/${encodeURIComponent(project.quote_number)}/approve-pdf`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Failed to approve PDF");
      }

      alert("PDF approved! Blocker cleared.");
      fetchProjects();
    } catch (err) {
      console.error("Error approving PDF:", err);
      alert(`Error: ${err.message}`);
    }
  }, [acquireAccessToken, fetchProjects]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "No date";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Calculate production status indicator
  const getStatusIndicator = (project) => {
    if (project.current_blocker) {
      return { color: "bg-red-500", label: "Blocked" };
    }
    const status = project.production_status_cache || "";
    if (status.toLowerCase().includes("complete")) {
      return { color: "bg-green-500", label: "Complete" };
    }
    return { color: "bg-blue-500", label: "In Progress" };
  };

  // Empty state
  if (!loading && projects.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <div className="text-6xl mb-4">🔄</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Active Projects</h2>
          <p className="text-gray-600 max-w-md">
            Projects in production will appear here after work orders are published.
          </p>
          <p className="text-sm text-gray-400 mt-4">
            Publish a work order from WO Review (Tab 3) to see it here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Project List (left panel) */}
      <div className="w-1/4 min-w-[280px] max-w-[350px] border-r border-gray-200 overflow-y-auto bg-white">
        <div className="p-3 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-700">
              In Production ({projects.length})
            </h3>
            <button
              onClick={fetchProjects}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-center text-gray-500">Loading projects...</div>
        ) : error ? (
          <div className="p-4 text-center text-red-500">{error}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {projects.map((project) => {
              const status = getStatusIndicator(project);
              const isSelected = selectedProject?.quote_number === project.quote_number;

              return (
                <div
                  key={project.quote_number}
                  onClick={() => setSelectedProject(project)}
                  className={`p-3 cursor-pointer transition-colors ${
                    isSelected
                      ? "bg-purple-50 border-l-4 border-purple-500"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {/* Status indicator */}
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${status.color}`} />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {project.quote_number}
                        </span>
                        {project.current_blocker && (
                          <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                            Blocked
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {project.customer_name}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {project.description || "No description"}
                      </p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>Ship: {formatDate(project.ship_date)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Panel (right panel) */}
      <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
        {selectedProject ? (
          <>
            {/* Project Header */}
            <div className="bg-white border-b border-gray-200 px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedProject.quote_number} - {selectedProject.customer_name}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {selectedProject.description || "No description"}
                  </p>
                </div>

                {/* PM Actions */}
                <div className="flex items-center gap-2">
                  {selectedProject.current_blocker === 'PDF_APPROVAL_PENDING' && (
                    <button
                      onClick={() => handleApprovePDF(selectedProject)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      Approve PDF
                    </button>
                  )}
                  <button
                    onClick={() => handleMarkComplete(selectedProject)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    Mark Complete
                  </button>
                </div>
              </div>

              {/* Blocker Alert */}
              {selectedProject.current_blocker && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-red-600">⚠️</span>
                    <span className="font-medium text-red-800">Blocker:</span>
                    <span className="text-red-700">{selectedProject.current_blocker}</span>
                  </div>
                </div>
              )}
            </div>

            {/* MilestoneGrid */}
            <div className="flex-1 overflow-auto p-4">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="font-medium text-gray-700 mb-4">Production Progress</h3>
                <MilestoneGrid
                  quoteNumber={selectedProject.quote_number}
                  token={accessToken}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>Select a project to view production progress</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
